'use client';

import { useEffect, useRef } from 'react';

const steps = [
  {
    number: '01',
    title: 'Free Consultation',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
      </svg>
    ),
    description:
      'We start with a no-obligation discovery call to understand your goals, timeline, and requirements. You speak directly with our project leads — no sales scripts.',
    details: ['30–60 min video or voice call', 'Requirement documentation', 'Timeline estimation', 'No commitment required'],
  },
  {
    number: '02',
    title: 'Proposal & 50% Deposit',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="9" y1="15" x2="15" y2="15"/>
      </svg>
    ),
    description:
      'We deliver a detailed written proposal including scope, deliverables, timeline, and fixed pricing. A 50% deposit is required to begin work — payable via Stripe or PayPal.',
    details: ['Detailed project scope document', 'Fixed USD pricing, no hidden fees', '50% deposit via Stripe or PayPal', 'Project kickoff within 48 hours'],
  },
  {
    number: '03',
    title: 'Development & Updates',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <polyline points="16 18 22 12 16 6"/>
        <polyline points="8 6 2 12 8 18"/>
      </svg>
    ),
    description:
      'Our team begins execution with regular progress updates via email. You receive milestone previews for feedback and approval before we proceed to the next phase.',
    details: ['Bi-weekly progress reports', 'Milestone-based previews', 'Revision rounds included', 'Direct communication via email'],
  },
  {
    number: '04',
    title: 'Delivery & Final Payment',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
    ),
    description:
      'Once you approve the final deliverables, the remaining 50% balance is due. We then transfer all files, credentials, and documentation — fully yours, no lock-in.',
    details: ['Final deliverables handover', '50% balance payment', 'Full asset & source file transfer', '30-day post-delivery support'],
  },
];

export default function ProcessSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.1 }
    );
    sectionRef?.current?.querySelectorAll('.reveal-hidden')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="process" ref={sectionRef} className="py-24 lg:py-32" style={{ background: '#fff' }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 reveal-hidden">
          <span className="section-label">How It Works</span>
          <div className="section-divider mx-auto" />
          <h2
            className="font-heading font-extrabold leading-tight mb-4"
            style={{ fontSize: 'clamp(2rem, 3.5vw, 2.8rem)', color: '#0D1A3A', letterSpacing: '-0.025em' }}
          >
            Simple, Structured,<br />Stress-Free Process.
          </h2>
          <p className="text-base max-w-lg mx-auto" style={{ color: '#6B7280' }}>
            From first contact to final delivery — every step is documented, transparent, and designed to protect both parties.
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-7">
          {steps?.map((step, idx) => (
            <div
              key={step?.number}
              className="step-card reveal-hidden"
              style={{ transitionDelay: `${idx * 100}ms` }}
            >
              <span className="step-number">{step?.number}</span>

              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
                style={{ background: '#EEF2FF', color: '#1F3A8A' }}
              >
                {step?.icon}
              </div>

              <h3
                className="font-heading font-bold mb-3"
                style={{ fontSize: '17px', color: '#0D1A3A', lineHeight: 1.3 }}
              >
                {step?.title}
              </h3>
              <p className="text-sm leading-relaxed mb-5" style={{ color: '#6B7280' }}>
                {step?.description}
              </p>

              <ul className="space-y-2">
                {step?.details?.map((d) => (
                  <li key={d} className="flex items-start gap-2">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1F3A8A" strokeWidth="2.5" strokeLinecap="round" className="flex-shrink-0 mt-0.5">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                    <span style={{ fontSize: '12.5px', color: '#6B7280' }}>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Payment note */}
        <div
          className="mt-12 p-6 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4 reveal-hidden"
          style={{ background: '#EEF2FF', border: '1px solid rgba(31,58,138,0.15)' }}
        >
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: '#1F3A8A' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                <rect x="1" y="4" width="22" height="16" rx="2"/>
                <line x1="1" y1="10" x2="23" y2="10"/>
              </svg>
            </div>
            <div>
              <p className="font-heading font-bold text-sm" style={{ color: '#0D1A3A' }}>50% Upfront · 50% on Delivery</p>
              <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>Secure payments via Stripe and PayPal — USD only. No stored card data.</p>
            </div>
          </div>
          <a href="#payment" className="btn-primary flex-shrink-0" style={{ fontSize: '14px', padding: '11px 22px' }}>
            Payment Details
          </a>
        </div>
      </div>
    </section>
  );
}